import React, { useRef } from 'react';

import {
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import AvatarRectangle from 'components/AvatarV3/RectangleAvatar';
import { TypographyEllipsis } from 'components/Templates/TypographyEllipsis';
import Tooltip from 'components/Tooltip';
import globalMessages from 'translations/messages/global-messages';

const getRowStyles = ({ theme, hideTextInSmallScreen }) => ({
  display: 'flex',
  flexGrow: 0,
  width: '100%',
  minWidth: 'auto',
  alignItems: 'center',
  padding: theme.spacing(2),

  '& .breadcrumb-row-icon': {
    minWidth: 0,
    marginLeft: theme.spacing(2),
  },

  '& .MuiListItemText-root': {
    margin: 0,
    ...(hideTextInSmallScreen && {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    }),
  },

  '& .breadcrumb-row-name': {
    lineHeight: '1.3em',
    display: 'block',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
  },

  '& .breadcrumb-row-infos': {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    marginLeft: theme.spacing(2),

    '& .breadcrumb-row-public-id': {
      lineHeight: '120%',
      display: 'block',
      alignItems: 'center',
    },

    '& .breadcrumb-row-members': {
      lineHeight: '120%',
      display: 'block',
      alignItems: 'center',
      overflow: 'visible',
    },
  },
});

const shouldForwardRowProp = prop => prop !== 'hideTextInSmallScreen';

const ListItem_Wrapper = styled(ListItem, { shouldForwardProp: shouldForwardRowProp })(
  getRowStyles,
);

const ListItemButton_Wrapper = styled(ListItemButton, {
  shouldForwardProp: shouldForwardRowProp,
})(getRowStyles);

const Divider_Wrapper = styled(Divider)({
  marginLeft: 0,
});

const BreadcrumbRow = ({
  className,
  entity,
  publicId,
  showDivider = false,
  showMember = false,
  hideAvatar = false,
  hideTextInSmallScreen = false,
  disableTooltips = false,
  isButton = true,
  onClick,
}) => {
  const intl = useIntl();

  const entityNameRef = useRef(null);
  const entityIdRef = useRef(null);

  const displayedPublicId = publicId || entity?.public_id;
  const RowWrapper = isButton ? ListItemButton_Wrapper : ListItem_Wrapper;

  return (
    <>
      <RowWrapper
        className={className}
        hideTextInSmallScreen={hideTextInSmallScreen}
        onClick={() => onClick?.(entity)}
      >
        {!hideAvatar && (
          <ListItemIcon className="breadcrumb-row-icon">
            <AvatarRectangle
              src={entity?.media_resource?.url || entity?.logo?.url || entity?.logo?.pre_signed_url}
              name={entity?.name || ''}
              alt={entity?.name}
            />
          </ListItemIcon>
        )}
        <ListItemText>
          <Tooltip
            title={entity?.name}
            forwardedRef={entityNameRef}
            placement="bottom"
            disableHoverListener={disableTooltips}
          >
            <TypographyEllipsis className="breadcrumb-row-name" variant="body" ref={entityNameRef}>
              {entity?.name || ''}
            </TypographyEllipsis>
          </Tooltip>
          <div className="breadcrumb-row-infos">
            <Tooltip
              title={displayedPublicId}
              forwardedRef={entityIdRef}
              placement="bottom"
              disableHoverListener={disableTooltips}
            >
              <TypographyEllipsis
                className="breadcrumb-row-public-id"
                variant="subtitleLight"
                ref={entityIdRef}
              >
                {displayedPublicId}
              </TypographyEllipsis>
            </Tooltip>
            {showMember && (
              <TypographyEllipsis className="breadcrumb-row-members" variant="subtitleLight">
                &nbsp;|&nbsp;
                {intl.formatMessage(globalMessages.groups_list_member, {
                  count: entity?.members_count,
                })}
              </TypographyEllipsis>
            )}
          </div>
        </ListItemText>
      </RowWrapper>
      {showDivider && <Divider_Wrapper variant="inset" component="li" />}
    </>
  );
};

BreadcrumbRow.propTypes = {
  className: PropTypes.string,
  entity: PropTypes.object,
  publicId: PropTypes.string,
  showDivider: PropTypes.bool,
  showMember: PropTypes.bool,
  hideAvatar: PropTypes.bool,
  hideTextInSmallScreen: PropTypes.bool,
  disableTooltips: PropTypes.bool,
  isButton: PropTypes.bool,
  onClick: PropTypes.func,
};

export default BreadcrumbRow;
